"use strict";
import React, { useCallback, useState } from "react";
import { ScrollView, Dimensions, StatusBar } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  cancelAllNotifications,
  scheduleGoalNotif,
} from "@microdoing/shared/services/notifService";
import { setNextCategoryRequest } from "@microdoing/shared/services/userCampaign/actions";
import { getNextCategories } from "@microdoing/shared/services/action/selectors";
import {
  Box,
  MainButton,
  MainText,
  MySafeAreaView,
  PressableOpacity,
} from "../../components/common";
import i18n from "@microdoing/shared/i18n";
import selectLanguage from "../../utils/selectLanguage";
import NextCategoryBox from "./NextCategoryBox";
import HeaderScreen from "app/components/common/HeaderScreen";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { breadCrumbsReflexiveAnalysisNextAction } from "app/navigation/breadcrumb/BreadcrumbPaths";
import normalize from "app/utils/normalize";
import { getUserCampaignId } from "@microdoing/shared/services/userCampaign/selectors";
import { getUserSkills } from "@microdoing/shared/services/user/selectors";
import { useNavigation } from "@react-navigation/native";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import theme from "app/Theme/Theme";

const { width } = Dimensions.get("screen");

const NUMBER_OF_CATEGORIES = 3;

const getRandomIntInclusive = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const getCategoriesAvailable = () => [
  {
    name: i18n.t("leadership"),
    value: "leadership",
  },
  {
    name: i18n.t("proactivity"),
    value: "proactivity",
  },
  {
    name: i18n.t("resilience"),
    value: "resilience",
  },
  {
    name: i18n.t("teamCohesion"),
    value: "teamCohesion",
  },
  {
    name: i18n.t("publicSpeaking"),
    value: "publicSpeaking",
  },
];

const getRandomCategories = skills => {
  let categories = [];
  if (skills.length > 0) {
    categories = [...skills];
  } else {
    categories = [...getCategoriesAvailable()];
  }
  const length = categories.length;
  let i = 0;
  const randomCategories = [];
  while (i < NUMBER_OF_CATEGORIES && categories.length > 0) {
    const random = getRandomIntInclusive(0, length - 1 - i);
    randomCategories.push(categories[random]);
    categories.splice(random, 1);
    i++;
  }

  return randomCategories;
};

const displayCategoriesChoice = (
  randomCategories,
  userSkills,
  language,
  skills,
  nextCategory,
  selectCategory,
  boxWidth,
) => {
  const result = [];
  result.push(
    randomCategories.map((category, index) => {
      let level = 0;
      let experience = 0;
      let progress = 0;
      let categoryTitle = category.name || "leadership";
      if (skills.length > 0) {
        const userSkillIndex = userSkills.findIndex(
          el => el.value === category.value,
        );
        if (userSkillIndex > -1) {
          experience = userSkills[userSkillIndex].experience;
          level = Math.floor(experience / 5);
          progress = (experience % 5) * 0.1;
        } else {
          level = 0;
          progress = 0;
        }
        categoryTitle = selectLanguage({ text: category.label, language });
      }

      return (
        <PressableOpacity
          testID={`next-category-bar-${index}`}
          key={index}
          onPress={selectCategory(category.value, categoryTitle)}
          style={styles.box}
        >
          <NextCategoryBox
            isSelected={category.value === nextCategory}
            isPlainColor={!nextCategory || category.value === nextCategory}
            key={index}
            categoryTitle={categoryTitle}
            experience={experience}
            progress={progress}
            boxWidth={boxWidth}
          />
        </PressableOpacity>
      );
    }),
  );
  return result;
};

const NextActionScreen = ({}) => {
  const [nextCategory, setNextCategory] = useState("");
  const [nextCategoryName, setNextCategoryName] = useState("");
  const [boxWidth, setBoxWidth] = useState(width * 0.9);

  const userCampaignId = useSelector(getUserCampaignId);
  const language = useSelector(getLanguage);
  const skills = useSelector(getNextCategories);
  const userSkills = useSelector(getUserSkills);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const selectCategory = (nextCategory, nextCategoryName) => async () => {
    setNextCategoryName(nextCategoryName);
    setNextCategory(nextCategory);
  };

  const onValidate = async () => {
    navigation.replace("Main");

    await cancelAllNotifications();
    await scheduleGoalNotif();

    dispatch(
      setNextCategoryRequest({
        nextCategory,
        userCampaignId,
      }),
    );
  };

  const randomCategories = useCallback(getRandomCategories(skills), [skills]);

  const findDimensions = ({ height, width: lWidth }) => {
    setBoxWidth(lWidth);
  };

  const handleOnLayout = event => {
    findDimensions(event.nativeEvent.layout);
  };

  return (
    <WebDesktopView breadcrumbsPat={breadCrumbsReflexiveAnalysisNextAction}>
      <StatusBar
        barStyle="dark-content"
        translucent
        backgroundColor={theme.colors["neutral.75"]}
      />
      <MySafeAreaView
        borderRadius={isWebDesktop ? 8 : 0}
        testID="next-action-screen"
        backgroundColor={isWebDesktop ? "neutral.white" : "neutral.75"}
        padding={isWebDesktop ? "xl" : "sm"}
        paddingHorizontal={isWebDesktop ? "xxl" : "m"}
        paddingTop={isWebDesktop ? "xl" : "null"}
        paddingBottom={isWebDesktop ? "sm" : "null"}
        flex={1}
      >
        <HeaderScreen title={i18n.t("next-action-title")} />

        <ScrollView onLayout={handleOnLayout}>
          <MainText
            variant="l4-medium"
            color="neutral.600"
            marginBottom="l"
            alignSelf="flex-start"
          >
            {i18n.t("next-action-select")}
          </MainText>
          {displayCategoriesChoice(
            randomCategories,
            userSkills,
            language,
            skills,
            nextCategory,
            selectCategory,
            boxWidth,
          )}
        </ScrollView>
        <Box
          flexDirection={isWebDesktop ? "row" : "column"}
          flex={1}
          width={isWebDesktop ? 320 : "100%"}
          justifyContent="flex-end"
        >
          <MainButton
            margin="null"
            marginBottom="sm"
            testID={"next-category-submit-button"}
            text={i18n.t("validate")}
            onPress={onValidate}
            isDisabled={!nextCategory && randomCategories.length}
            textColor={!nextCategory ? "neutral.900" : "neutral.white"}
          />
        </Box>
      </MySafeAreaView>
    </WebDesktopView>
  );
};

const styles = {
  topView: {
    alignItems: "center",
  },
  box: {
    width: "100%",
    paddingTop: 10,
    marginBottom: 10,
  },
};

export default NextActionScreen;
